import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Ticket, Plus, Copy, Check, Clock, UserPlus, Trash2, AlertCircle, Loader2, Share2, Church, HelpCircle, ShieldCheck } from "lucide-react";
import { createInvite, getMyInvites, revokeInvite, getMyParishesForInvite } from "@/lib/api";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { ParishSearchPicker } from "@/components/ParishSearchPicker";

export default function InvitePage({ user }) {
  const [invites, setInvites] = useState([]);
  const [parishes, setParishes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [selectedParishId, setSelectedParishId] = useState("");
  const [inviteNote, setInviteNote] = useState("");
  const [copiedCode, setCopiedCode] = useState(null);
  const [newInvite, setNewInvite] = useState(null);
  const [confirmationChecked, setConfirmationChecked] = useState(false);
  const [sameParish, setSameParish] = useState("yes"); // "yes" or "no"
  const [differentParishId, setDifferentParishId] = useState("");

  // Check if user is a verified parish member
  const isVerifiedMember = parishes.length > 0;

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      // Load parishes first
      const parishRes = await getMyParishesForInvite();
      const userParishes = parishRes.data.parishes || [];
      setParishes(userParishes);
      
      // Auto-select if only one parish
      if (userParishes.length === 1) {
        setSelectedParishId(userParishes[0].parish_id);
      }
      
      // Load invites if user has parishes
      if (userParishes.length > 0) {
        const inviteRes = await getMyInvites();
        setInvites(inviteRes.data.invites || []);
      }
    } catch (err) {
      console.error("Failed to load data:", err);
    }
    setLoading(false);
  };

  const handleCreateInvite = async () => {
    if (!selectedParishId) {
      toast.error("Please select a parish");
      return;
    }
    if (!confirmationChecked) {
      toast.error("Please confirm the declaration");
      return;
    }
    setCreating(true);
    try {
      const res = await createInvite({ 
        parish_id: selectedParishId,
        target_parish_id: sameParish === "no" && differentParishId ? differentParishId : null,
        note: inviteNote.trim() || null,
        assigns_parish: sameParish === "yes" || !!differentParishId
      });
      setNewInvite(res.data);
      setInviteNote("");
      setConfirmationChecked(false);
      setSameParish("yes");
      loadData();
      toast.success(`Invite created!`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create invite");
    }
    setCreating(false);
    setCreateDialogOpen(false);
  };

  const handleRevoke = async (inviteCode) => {
    if (!window.confirm("Revoke this invite? It will no longer be usable.")) return;
    try {
      await revokeInvite(inviteCode);
      toast.success("Invite revoked");
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to revoke invite");
    }
  };

  const copyToClipboard = async (code) => {
    const inviteUrl = `${window.location.origin}/register?invite=${code}`;
    try {
      await navigator.clipboard.writeText(inviteUrl);
      setCopiedCode(code);
      toast.success("Invite link copied!");
      setTimeout(() => setCopiedCode(null), 2000);
    } catch {
      toast.error("Failed to copy");
    }
  };

  const shareInvite = async (code) => {
    const inviteUrl = `${window.location.origin}/register?invite=${code}`;
    const shareData = {
      title: "Join Parish",
      text: "You're invited to Parish!",
      url: inviteUrl
    };
    
    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch (err) {
        if (err.name !== 'AbortError') {
          copyToClipboard(code);
        }
      }
    } else {
      copyToClipboard(code);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const isExpired = (dateStr) => new Date(dateStr) < new Date();

  const pendingInvites = invites.filter(i => !i.is_used && !isExpired(i.expires_at));
  const usedInvites = invites.filter(i => i.is_used);
  const expiredInvites = invites.filter(i => !i.is_used && isExpired(i.expires_at));

  // Not a verified member
  if (!isVerifiedMember) {
    return (
      <div className="min-h-screen bg-[#F9F7F2]" data-testid="invite-page">
        <div className="max-w-2xl mx-auto px-4 py-8">
          <Card className="bg-white border-[#E5E0D5]">
            <CardContent className="py-12 text-center">
              <AlertCircle className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
              <h2 className="font-['Crimson_Text'] text-xl font-bold text-[#2D2A26] mb-2">
                Join a Parish First
              </h2>
              <p className="text-[#6B665E] font-['Nunito'] mb-4">
                You need to be a verified member of a parish before you can invite others.
              </p>
              <p className="text-sm text-[#6B665E] font-['Nunito'] mb-4">
                Visit the Parishes page to find and join your local parish.
              </p>
              <Link 
                to="/settings?section=support&category=parish_request"
                className="text-sm text-[var(--liturgical-primary)] hover:underline font-['Nunito'] inline-flex items-center gap-1"
                data-testid="request-add-parish-link-unverified"
              >
                <HelpCircle className="w-3.5 h-3.5" />
                Can't find your parish? Request to add it.
              </Link>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9F7F2]" data-testid="invite-page">
      <div className="max-w-2xl mx-auto px-4 py-8 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-['Crimson_Text'] text-3xl font-bold text-[#2D2A26] tracking-tight">
              Invite Friends
            </h1>
            <p className="text-sm text-[#6B665E] font-['Nunito'] mt-1">
              Share Parish with people you trust
            </p>
          </div>
          <Button 
            onClick={() => setCreateDialogOpen(true)}
            className="bg-[var(--liturgical-primary)] hover:brightness-90 text-white font-['Nunito']"
            data-testid="create-invite-btn"
          >
            <Plus className="w-4 h-4 mr-2" /> New Invite
          </Button>
        </div>

        {/* New Invite Success Card */}
        {newInvite && (
          <Card className="bg-green-50 border-green-200 animate-in slide-in-from-top-2">
            <CardContent className="p-4">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-['Nunito'] font-semibold text-green-800 mb-1">Invite Created!</h3>
                  <p className="text-sm text-green-700 mb-1">
                    Invite to join <strong>{newInvite.parish_name}</strong>
                  </p>
                  <p className="text-sm text-green-700 mb-2">Share this link:</p>
                  <code className="bg-white px-3 py-1.5 rounded text-sm font-mono break-all block">
                    {window.location.origin}/register?invite={newInvite.invite_code}
                  </code>
                  <p className="text-xs text-green-600 mt-2">
                    Expires in {newInvite.expires_in_days} days
                  </p>
                </div>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => shareInvite(newInvite.invite_code)}
                  className="border-green-300 text-green-700 hover:bg-green-100"
                >
                  <Share2 className="w-4 h-4 mr-1" /> Share
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Stats */}
        <div className="grid grid-cols-3 gap-2 sm:gap-4">
          <Card className="bg-white border-[#E5E0D5]">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-[var(--liturgical-primary)]">{pendingInvites.length}</p>
              <p className="text-xs text-[#6B665E] font-['Nunito']">Pending</p>
            </CardContent>
          </Card>
          <Card className="bg-white border-[#E5E0D5]">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-green-600">{usedInvites.length}</p>
              <p className="text-xs text-[#6B665E] font-['Nunito']">Joined</p>
            </CardContent>
          </Card>
          <Card className="bg-white border-[#E5E0D5]">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-[#6B665E]">{expiredInvites.length}</p>
              <p className="text-xs text-[#6B665E] font-['Nunito']">Expired</p>
            </CardContent>
          </Card>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <Loader2 className="w-8 h-8 text-[var(--liturgical-primary)] animate-spin mx-auto" />
          </div>
        ) : invites.length === 0 ? (
          <Card className="bg-white border-[#E5E0D5]">
            <CardContent className="py-12 text-center">
              <Ticket className="w-12 h-12 text-[#E5E0D5] mx-auto mb-3" />
              <h3 className="font-['Nunito'] font-semibold text-[#2D2A26] mb-1">No invites yet</h3>
              <p className="text-sm text-[#6B665E] font-['Nunito']">
                Create your first invite to bring someone into the Parish community.
              </p>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Pending Invites */}
            {pendingInvites.length > 0 && (
              <Card className="bg-white border-[#E5E0D5]">
                <CardHeader className="pb-2">
                  <CardTitle className="font-['Nunito'] text-lg flex items-center gap-2">
                    <Clock className="w-5 h-5 text-yellow-500" />
                    Pending Invites ({pendingInvites.length})
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {pendingInvites.map((invite) => (
                    <div 
                      key={invite.invite_code} 
                      className="p-3 bg-yellow-50 rounded-lg border border-yellow-200"
                      data-testid={`pending-invite-${invite.invite_code}`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <code className="text-sm font-mono text-[#2D2A26]">{invite.invite_code}</code>
                          <Badge variant="outline" className="text-xs">
                            <Church className="w-3 h-3 mr-1" />
                            {invite.parish_name}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => shareInvite(invite.invite_code)}
                            className="h-8 px-2"
                          >
                            {copiedCode === invite.invite_code ? (
                              <Check className="w-4 h-4 text-green-600" />
                            ) : (
                              <Share2 className="w-4 h-4" />
                            )}
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => handleRevoke(invite.invite_code)}
                            className="h-8 px-2 text-red-500 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                      <div className="flex items-center justify-between text-xs text-[#6B665E]">
                        <span>Expires {formatDate(invite.expires_at)}</span>
                        {invite.note && <span className="italic">"{invite.note}"</span>}
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}

            {/* Used Invites */}
            {usedInvites.length > 0 && (
              <Card className="bg-white border-[#E5E0D5]">
                <CardHeader className="pb-2">
                  <CardTitle className="font-['Nunito'] text-lg flex items-center gap-2">
                    <UserPlus className="w-5 h-5 text-green-600" />
                    People You've Invited ({usedInvites.length})
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {usedInvites.map((invite) => (
                    <div 
                      key={invite.invite_code} 
                      className="p-3 bg-green-50 rounded-lg border border-green-200"
                      data-testid={`used-invite-${invite.invite_code}`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-['Nunito'] font-semibold text-[#2D2A26]">
                            {invite.used_by_name}
                          </p>
                          <p className="text-xs text-[#6B665E]">{invite.used_by_email}</p>
                        </div>
                        <Badge className="bg-green-100 text-green-800 border-0">
                          Joined {formatDate(invite.used_at)}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}

            {/* Expired Invites (collapsed) */}
            {expiredInvites.length > 0 && (
              <Card className="bg-white/50 border-[#E5E0D5]">
                <CardHeader className="pb-2">
                  <CardTitle className="font-['Nunito'] text-lg text-[#6B665E] flex items-center gap-2">
                    Expired ({expiredInvites.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-[#6B665E]">
                    These invites have expired and can no longer be used.
                  </p>
                </CardContent>
              </Card>
            )}
          </>
        )}

        {/* Create Invite Dialog */}
        <Dialog open={createDialogOpen} onOpenChange={(open) => {
          setCreateDialogOpen(open);
          if (!open) {
            setConfirmationChecked(false);
            setSameParish("yes");
          }
        }}>
          <DialogContent className="bg-white border-[#E5E0D5] max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-['Crimson_Text'] text-xl text-[#2D2A26]">
                Create New Invite
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              {/* Parish Selection */}
              {parishes.length > 1 && (
                <div className="space-y-2">
                  <Label className="text-sm font-['Nunito'] text-[#2D2A26]">
                    Your Parish *
                  </Label>
                  <Select value={selectedParishId} onValueChange={setSelectedParishId}>
                    <SelectTrigger className="border-[#E5E0D5]">
                      <SelectValue placeholder="Select a parish" />
                    </SelectTrigger>
                    <SelectContent>
                      {parishes.map((parish) => (
                        <SelectItem key={parish.parish_id} value={parish.parish_id}>
                          <div className="flex items-center gap-2">
                            <Church className="w-4 h-4" />
                            {parish.name}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              
              {/* Single parish info */}
              {parishes.length === 1 && (
                <div className="bg-[#F9F7F2] rounded-lg p-3 flex items-center gap-2">
                  <Church className="w-5 h-5 text-[var(--liturgical-primary)]" />
                  <span className="font-['Nunito'] text-sm">
                    Your parish: <strong>{parishes[0].name}</strong>
                  </span>
                </div>
              )}
              
              {/* Same Parish Question */}
              <div className="space-y-3">
                <Label className="text-sm font-['Nunito'] text-[#2D2A26]">
                  Does this person attend the same parish as you? *
                </Label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="sameParish"
                      value="yes"
                      checked={sameParish === "yes"}
                      onChange={(e) => setSameParish(e.target.value)}
                      className="w-4 h-4 text-[var(--liturgical-primary)]"
                    />
                    <span className="font-['Nunito'] text-sm">Yes, same parish</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="sameParish"
                      value="no"
                      checked={sameParish === "no"}
                      onChange={(e) => setSameParish(e.target.value)}
                      className="w-4 h-4 text-[var(--liturgical-primary)]"
                    />
                    <span className="font-['Nunito'] text-sm">No / I'm not sure</span>
                  </label>
                </div>
                
                {/* Info based on selection */}
                <div className={`rounded-lg p-3 text-sm font-['Nunito'] ${sameParish === "yes" ? "bg-green-50 border border-green-200" : "bg-blue-50 border border-blue-200"}`}>
                  {sameParish === "yes" ? (
                    <p className="text-green-800">
                      <strong>Auto-join enabled:</strong> When they sign up, they will automatically become a member of your parish.
                    </p>
                  ) : (
                    <div className="space-y-3">
                      <p className="text-blue-800">
                        Choose their parish below, or leave blank if unsure. They can also request to join a parish after signing up.
                      </p>
                      <ParishSearchPicker
                        value={differentParishId}
                        onChange={(id) => setDifferentParishId(id)}
                        label="Assign to parish (optional)"
                        placeholder="Search by name, city, or zip..."
                      />
                    </div>
                  )}
                </div>
              </div>
              
              {/* Request to Add Parish Link */}
              <div className="text-center">
                <Link 
                  to="/settings?section=support&category=parish_request"
                  className="text-sm text-[var(--liturgical-primary)] hover:underline font-['Nunito'] inline-flex items-center gap-1"
                  data-testid="request-add-parish-link"
                >
                  <HelpCircle className="w-3.5 h-3.5" />
                  Don't see a parish? Request to add it.
                </Link>
              </div>
              
              <div className="space-y-2">
                <Label className="text-xs font-['Nunito'] text-[#6B665E]">
                  Note (optional)
                </Label>
                <Textarea
                  value={inviteNote}
                  onChange={(e) => setInviteNote(e.target.value)}
                  placeholder="Who is this invite for? e.g., 'For my cousin John'"
                  className="border-[#E5E0D5] font-['Nunito']"
                  rows={2}
                />
                <p className="text-xs text-[#6B665E]">
                  This note is only visible to you.
                </p>
              </div>
              
              {/* Confirmation Checkbox */}
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <Checkbox
                    id="confirmation"
                    checked={confirmationChecked}
                    onCheckedChange={setConfirmationChecked}
                    className="mt-0.5 data-[state=checked]:bg-[var(--liturgical-primary)] data-[state=checked]:border-[var(--liturgical-primary)]"
                    data-testid="invite-confirmation-checkbox"
                  />
                  <label 
                    htmlFor="confirmation" 
                    className="text-sm font-['Nunito'] text-[#2D2A26] cursor-pointer leading-relaxed"
                  >
                    <strong>I confirm</strong> that the person I am inviting is a <strong>confirmed Catholic</strong>. I understand that invites should only be shared with people I personally know and trust.
                  </label>
                </div>
              </div>
              
              <div className="bg-[#F9F7F2] rounded-lg p-3">
                <p className="text-sm text-[#6B665E] font-['Nunito']">
                  <strong>Note:</strong> Invites expire after 15 days and can only be used once.
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleCreateInvite}
                disabled={creating || (parishes.length > 1 && !selectedParishId) || !confirmationChecked}
                className="bg-[var(--liturgical-primary)] hover:brightness-90"
              >
                {creating ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Creating...
                  </>
                ) : (
                  <>
                    <Ticket className="w-4 h-4 mr-2" /> Create Invite
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
